<template>
    <footer class="relative border-t backdrop-blur-xl py-8 mt-auto"
            style="background: rgba(7,3,15,0.98); border-color: rgba(167,93,237,0.18);">

        <!-- Top glow line -->
        <div class="absolute top-0 left-0 right-0 h-px"
             style="background: linear-gradient(90deg, transparent 0%, rgba(167,93,237,0.5) 25%, rgba(232,121,249,0.6) 50%, rgba(167,93,237,0.5) 75%, transparent 100%);"></div>

        <div class="px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
            <div class="flex flex-col sm:flex-row justify-between items-center gap-5">

                <!-- Brand + copyright -->
                <div class="flex items-center gap-3">
                    <div class="w-7 h-7 rounded-lg flex items-center justify-center"
                         style="background:linear-gradient(135deg,#7c3aed,#a75ded,#e879f9);">
                        <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2L2 7l10 5 10-5-10-5z" opacity="0.9"/>
                            <path d="M2 17l10 5 10-5" opacity="0.7"/>
                            <path d="M2 12l10 5 10-5"/>
                        </svg>
                    </div>
                    <p class="text-sm text-white/40">
                        © {{ currentYear }} <span class="font-bold text-white/60">{{ appName }}</span>. {{ t('footerAllRightsReserved') }}
                    </p>
                </div>

                <!-- Links -->
                <div class="flex items-center gap-4 sm:gap-6">
                    <Link :href="`/privacy-policy`"
                          class="text-sm text-white/50 font-medium transition-all duration-200 hover:text-purple-400">
                        {{ t('footerPrivacyPolicy') }}
                    </Link>
                    <Link :href="`/terms`"
                          class="text-sm text-white/50 font-medium transition-all duration-200 hover:text-purple-400">
                        {{ t('footerTermsService') }}
                    </Link>
                    <Link :href="`/cookies`"
                          class="text-sm text-white/50 font-medium transition-all duration-200 hover:text-purple-400">
                        {{ t('footerCookiePolicy') }}
                    </Link>
                </div>

                <!-- Language selector -->
                <div class="relative" ref="languageDropdown">
                    <button
                        ref="languageButton"
                        @click="toggleLanguageDropdown"
                        class="flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-300 border text-sm"
                        style="background: rgba(167,93,237,0.08); color: rgba(255,255,255,0.65); border-color: rgba(167,93,237,0.25);"
                        :style="showLanguageDropdown ? { borderColor:'rgba(167,93,237,0.5)', background:'rgba(167,93,237,0.14)' } : {}">
                        <span class="text-base">{{ currentLanguage.flag }}</span>
                        <span class="font-medium">{{ currentLanguage.code.toUpperCase() }}</span>
                        <i class="fas fa-chevron-down text-xs transition-transform duration-200"
                           :class="{ 'rotate-180': showLanguageDropdown }"
                           style="color:#a75ded;"></i>
                    </button>

                    <Teleport to="body">
                        <Transition
                            enter-active-class="transition ease-out duration-200"
                            enter-from-class="opacity-0 translate-y-1"
                            enter-to-class="opacity-100 translate-y-0"
                            leave-active-class="transition ease-in duration-150"
                            leave-from-class="opacity-100 translate-y-0"
                            leave-to-class="opacity-0 translate-y-1"
                        >
                            <div v-show="showLanguageDropdown"
                                 ref="dropdownMenu"
                                 :style="dropdownStyle"
                                 class="fixed w-48 backdrop-blur-xl border rounded-2xl shadow-2xl z-[9999]"
                                 style="background: rgba(10,4,22,0.98); border-color: rgba(167,93,237,0.3); box-shadow:0 20px 60px rgba(0,0,0,0.6),0 0 30px rgba(167,93,237,0.1);">
                                <div class="p-1.5">
                                    <button v-for="lang in languages" :key="lang.code"
                                            @click="handleLanguageChange(lang.code)"
                                            class="w-full px-3 py-2.5 text-left flex items-center gap-3 rounded-xl transition-all duration-200"
                                            :style="currentLanguage.code === lang.code
                                                ? 'background:rgba(167,93,237,0.18);color:#fff;'
                                                : 'color:rgba(255,255,255,0.6);'"
                                            :class="currentLanguage.code !== lang.code ? 'hover:bg-white/5' : ''">
                                        <span class="text-lg">{{ lang.flag }}</span>
                                        <div class="flex-1 min-w-0">
                                            <div class="text-sm font-medium">{{ lang.name }}</div>
                                            <div class="text-xs text-white/30">{{ lang.nativeName }}</div>
                                        </div>
                                        <svg v-if="currentLanguage.code === lang.code" class="w-3 h-3 flex-shrink-0" style="color:#a75ded;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </Transition>
                    </Teleport>
                </div>
            </div>
        </div>
    </footer>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import { useTranslation } from '@/composables/useTranslation'

const page = usePage()
const { t, currentLanguage, languages, changeLanguage } = useTranslation()

const appName     = computed(() => page.props.appName || 'TokenHive')
const currentYear = new Date().getFullYear()

const showLanguageDropdown = ref(false)
const languageDropdown     = ref(null)
const languageButton       = ref(null)
const dropdownMenu         = ref(null)
const dropdownStyle        = ref({})

const updateDropdownPosition = () => {
    if (!languageButton.value || !showLanguageDropdown.value) return
    const rect = languageButton.value.getBoundingClientRect()
    dropdownStyle.value = {
        top:   `${rect.top - 8}px`,
        left:  `${rect.right - 192}px`,
        transform: 'translateY(-100%)',
    }
}

const toggleLanguageDropdown = () => {
    showLanguageDropdown.value = !showLanguageDropdown.value
    if (showLanguageDropdown.value) nextTick(updateDropdownPosition)
}

const handleLanguageChange = async (langCode) => {
    try { await changeLanguage(langCode) } catch {}
    showLanguageDropdown.value = false
}

const handleClickOutside = (e) => {
    if (languageDropdown.value && !languageDropdown.value.contains(e.target) &&
        dropdownMenu.value && !dropdownMenu.value.contains(e.target)) {
        showLanguageDropdown.value = false
    }
}

const handleScrollResize = () => { if (showLanguageDropdown.value) updateDropdownPosition() }

watch(showLanguageDropdown, v => { if (v) nextTick(updateDropdownPosition) })

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    window.addEventListener('scroll', handleScrollResize, true)
    window.addEventListener('resize', handleScrollResize)
})
onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    window.removeEventListener('scroll', handleScrollResize, true)
    window.removeEventListener('resize', handleScrollResize)
})
</script>

<style scoped>
.rotate-180 { transform: rotate(180deg); }
</style>
